import { useMemo, useState, useEffect, useRef } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useMessageEditors, useDeleteMessageEditor, ACTION_TYPES, errMessage, type MessageEditorRow } from './systemMasters';
import { MessageEditorDetailDialog } from './MessageEditorDetailDialog';

interface Props { onHome?: () => void; }

const actionName = (v: number) => ACTION_TYPES.find((a) => a.v === v)?.t ?? String(v);

/**
 * Settings ▸ System Management ▸ Message Editor — LIMS MessageEditor_ListView.
 * CRUD list of application messages (MSID auto-generated). Columns MSID / Message /
 * MessageKey / ActionType; New / Edit / Delete / Refresh / Export / Search. The detail
 * form (Module/BO cascade, ActionType, Remark, Translations) opens ready to edit from the row icon or the toolbar
 * Edit (one ticked row), read-only from a row click (owner request 2026-10-15).
 */
export function MessageEditorPage({ onHome }: Props) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const { data: rows = [], isFetching, refetch } = useMessageEditors();
  const del = useDeleteMessageEditor();
  const loadMs = useFetchMs(isFetching);
  const [search, setSearch] = useState('');
  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  const [editId, setEditId] = useState<string | null | undefined>(undefined);   // undefined=closed, null=New, guid=Edit
  // How a record opens: a row click / Detail View = read-only; the row icon / toolbar Edit = ready to edit.
  const [openMode, setOpenMode] = useState<'view' | 'edit'>('view');
  const openRecord = (id: string, m: 'view' | 'edit') => { setOpenMode(m); setEditId(id); };
  // Toolbar Edit: the ONE ticked record opens in the Detail View, editable (owner request 2026-10-15).
  const editSelected = () => { if (selectedIds.size === 1) openRecord(String([...selectedIds][0]), 'edit'); };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.msid, r.message, r.messageKey, actionName(r.actionType)]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const doDelete = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one message to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} message${selectedIds.size === 1 ? '' : 's'}?` })) return;
    try {
      for (const id of selectedIds) await del.mutateAsync(String(id));
      setSelectedIds(new Set());
      await alert({ title: 'Success', message: 'Deleted successfully.', tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };

  // LIMS MessageEditor_ListView shows NO sort symbol on any column (filter-only); rows default to MSID order.
  const columns: AppColumn<MessageEditorRow>[] = [
    {
      // Row icon (G5): opens the record in the Detail View, ready to edit (owner request 2026-10-15).
      field: 'detailedit', header: '', headerText: 'Edit', noExport: true, sortable: false, filterable: false, width: 44, align: 'center', text: () => '',
      render: (r) => (
        <button className="pts-icon-btn edit" title="Edit" aria-label={`Edit message ${r.msid ?? ''}`}
          onClick={(e) => { e.stopPropagation(); openRecord(r.id, 'edit'); }}><i className="ti ti-edit" /></button>
      ),
    },
    { field: 'msid', header: 'MSID', width: '8%', minWidth: 70, sortable: false, text: (r) => r.msid ?? '',
      compare: (a, b) => (a.msid ?? '').localeCompare(b.msid ?? '', undefined, { numeric: true }) },
    { field: 'message', header: 'Message', width: '46%', minWidth: 240, sortable: false, text: (r) => r.message ?? '' },
    { field: 'messageKey', header: 'MessageKey', width: '26%', minWidth: 140, sortable: false, text: (r) => r.messageKey ?? '' },
    { field: 'actionType', header: 'ActionType', width: '20%', minWidth: 120, sortable: false, text: (r) => actionName(r.actionType) },
  ];

  // New, or a single click on a row: the full-page detail replaces the list while open (standing
  // rule 12; owner request 2026-10-12). A row opens READ-ONLY with an Edit button; New opens
  // editable. `key` re-seeds it when another record is opened.
  if (editId !== undefined) {
    return <MessageEditorDetailDialog key={editId ?? 'new'} id={editId} initialMode={openMode} onHome={onHome} onClose={() => setEditId(undefined)} />;
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Message Editor</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              if (id) openRecord(id, 'view');
            }} />
          <FormTitle pageId="sm-message-editor" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Message Editor" />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        {!gridMax && (
        <AppToolbar
          onNew={() => setEditId(null)}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void doDelete()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="MessageEditor" headers={['MSID', 'Message', 'MessageKey', 'ActionType']}
                rows={visible.map((r) => [r.msid ?? '', r.message ?? '', r.messageKey ?? '', actionName(r.actionType)])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="MessageEditor" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<MessageEditorRow> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }}
            onRowClick={(r) => openRecord(r.id, 'view')}
            initialSort={{ key: 'msid', dir: 'asc' }} loading={isFetching} emptyText="No messages found." hideHeaderTools />
        </div>
      </div>

    </div>
  );
}
